import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Activity, CheckCircle2, XCircle, RefreshCw } from "lucide-react";

function StepRow({ name, step }) {
  const ok = step && step.ok;
  return (
    <div
      className="flex items-start justify-between gap-3 py-2 border-b border-slate-200/70 last:border-0"
      data-testid={`health-step-${name}`}
    >
      <div className="flex items-center gap-2">
        {ok ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        ) : (
          <XCircle className="h-4 w-4 text-rose-600" />
        )}
        <span className="text-sm font-medium capitalize">{name}</span>
      </div>
      {!ok && step?.error ? (
        <span className="text-[11px] font-mono text-rose-700 max-w-[60%] break-words text-right">
          {step.error}
        </span>
      ) : (
        <span className="text-[11px] font-mono text-neutral-500">OK</span>
      )}
    </div>
  );
}

export function HealthCheckDialog({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/health");
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !data && !loading) {
      run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
      }}
    >
      <DialogContent
        className="sm:max-w-lg rounded-xl border-slate-300/70"
        data-testid="health-check-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Activity className="h-5 w-5" /> Database Health
          </DialogTitle>
          <DialogDescription className="text-xs text-neutral-600">
            Verifies MongoDB connectivity and read/write/update/delete privileges. Useful for
            diagnosing production deployment issues.
          </DialogDescription>
        </DialogHeader>

        {loading && !data && (
          <div className="py-8 text-center text-sm text-neutral-500">Running probe…</div>
        )}

        {data && (
          <div className="space-y-4">
            <div
              className={`flex items-center justify-between px-3 py-2 border ${
                data.ok ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"
              }`}
              data-testid="health-overall"
            >
              <div className="flex items-center gap-2">
                {data.ok ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                ) : (
                  <XCircle className="h-4 w-4 text-rose-700" />
                )}
                <span className="text-sm font-semibold">
                  {data.ok ? "All checks passed" : "One or more checks failed"}
                </span>
              </div>
              <span className="text-[11px] font-mono text-neutral-600">
                {data.db_name}
              </span>
            </div>

            <div className="glass-panel rounded-xl">
              <div className="px-3 py-2 bg-white/50 text-[11px] font-mono uppercase tracking-wider text-neutral-500">
                Probe steps
              </div>
              <div className="px-3">
                {Object.entries(data.steps || {}).map(([name, step]) => (
                  <StepRow key={name} name={name} step={step} />
                ))}
              </div>
            </div>

            <div className="glass-panel">
              <div className="px-3 py-2 bg-white/50 text-[11px] font-mono uppercase tracking-wider text-neutral-500">
                Collection counts
              </div>
              <div className="px-3 py-2 grid grid-cols-2 gap-2 text-sm">
                {Object.entries(data.counts || {}).map(([name, count]) => (
                  <div
                    key={name}
                    className="flex justify-between"
                    data-testid={`health-count-${name}`}
                  >
                    <span className="text-neutral-600 capitalize">{name}</span>
                    <span className="font-mono">{String(count)}</span>
                  </div>
                ))}
              </div>
            </div>

            {data.admin_user && typeof data.admin_user === "object" && (
              <div className="glass-panel px-3 py-2 text-xs">
                <div className="text-[11px] font-mono uppercase tracking-wider text-neutral-500 mb-1">
                  Admin record
                </div>
                <div className="font-mono break-all">
                  {data.admin_user.email} · {data.admin_user.id?.slice(0, 8)}…
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end">
          <Button
            onClick={run}
            disabled={loading}
            variant="outline"
            className="rounded-xl border-slate-300/70"
            data-testid="health-rerun-button"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Running…" : "Re-run"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default HealthCheckDialog;
